import api from './api';
import type { ThemeChannel } from '../utils/themePaths';

export interface Theme {
  _id: string;
  name: string;
  channel?: ThemeChannel;
  description: string;
  themeImage?: {
    url: string;
    path: string;
  };
  customFrames: Array<{
    customFrameId: {
      _id: string;
      name: string;
      size: string;
      width?: number;
      height?: number;
      photoPositions: Array<{
        x: number;
        y: number;
        width: number;
        height: number;
      }>;
      frameImage?: {
        url: string;
        path: string;
      };
    };
    userFrameIds: Array<{
      _id: string;
      name: string;
      description: string;
      frameImage: {
        url: string;
        path: string;
      };
      tags: string[];
      metadata: {
        originalFileName: string;
        fileSize: number;
        uploadDate: string;
      };
    }>;
  }>;
  createdBy: string;
  isActive: boolean;
  mobileUploadProEnabled?: boolean;
  mobileUploadLiteEnabled?: boolean;
  /** Bật thì theme chỉ dùng trong khoảng availableFrom–availableUntil (mọi máy). */
  scheduleEnabled?: boolean;
  availableFrom?: string | null;
  availableUntil?: string | null;
  /** Bật thì chỉ các máy trong allowedMachineCodes được dùng theme. */
  machineFilterEnabled?: boolean;
  allowedMachineCodes?: string[];
  tags: string[];
  metadata: {
    totalCustomFrames: number;
    totalUserFrames: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateThemeData {
  name: string;
  description?: string;
  tags?: string[];
  themeImage?: File;
  channel?: ThemeChannel;
}

export interface UpdateThemeData {
  name?: string;
  description?: string;
  tags?: string[];
  isActive?: boolean;
  themeImage?: File;
}

export interface AddUserFramesData {
  themeId: string;
  customFrameId: string;
  userFrameIds: string[];
}

export interface RemoveUserFramesData {
  themeId: string;
  customFrameId: string;
  userFrameIds: string[];
}

export interface ThemeServiceContext {
  adminUserId?: string;
  channel?: ThemeChannel;
}

function themesRoot(ctx?: ThemeServiceContext) {
  return ctx?.adminUserId ? `/admin/users/${ctx.adminUserId}/themes` : '/themes';
}

function withChannelQuery(
  params?: Record<string, string | number | boolean | undefined>,
  ctx?: ThemeServiceContext,
) {
  const queryParams = buildQuery(params);
  const channel = ctx?.channel || 'pro';
  if (!queryParams.has('channel')) {
    queryParams.set('channel', channel);
  }
  return queryParams;
}

function buildQuery(params?: Record<string, string | number | boolean | undefined>) {
  const queryParams = new URLSearchParams();
  if (!params) return queryParams;
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) queryParams.append(key, String(value));
  });
  return queryParams;
}

export const themeService = {
  getThemes: async (
    params?: {
      page?: number;
      limit?: number;
      search?: string;
      tags?: string;
      isActive?: boolean;
      channel?: ThemeChannel;
    },
    ctx?: ThemeServiceContext,
  ) => {
    const queryParams = withChannelQuery(params, ctx);
    const response = await api.get(`${themesRoot(ctx)}?${queryParams.toString()}`);
    return response.data;
  },

  getTheme: async (themeId: string, ctx?: ThemeServiceContext) => {
    const response = await api.get(`${themesRoot(ctx)}/${themeId}`);
    return response.data;
  },

  createTheme: async (data: Omit<CreateThemeData, 'themeImage'>, ctx?: ThemeServiceContext) => {
    const response = await api.post(themesRoot(ctx), {
      ...data,
      channel: data.channel || ctx?.channel || 'pro',
    });
    return response.data;
  },

  createThemeWithImage: async (data: CreateThemeData, ctx?: ThemeServiceContext) => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('channel', data.channel || ctx?.channel || 'pro');
    if (data.description) formData.append('description', data.description);
    if (data.tags) formData.append('tags', data.tags.join(','));
    if (data.themeImage) formData.append('themeImage', data.themeImage);

    const response = await api.post(themesRoot(ctx), formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  updateTheme: async (
    themeId: string,
    data: Omit<UpdateThemeData, 'themeImage'>,
    ctx?: ThemeServiceContext,
  ) => {
    const response = await api.put(`${themesRoot(ctx)}/${themeId}`, data);
    return response.data;
  },

  setThemeActive: async (themeId: string, isActive: boolean, ctx?: ThemeServiceContext) => {
    const response = await api.patch(`${themesRoot(ctx)}/${themeId}/active`, {
      isActive,
      source: 'web',
    });
    return response.data;
  },

  setMobileUploadProEnabled: async (themeId: string, enabled: boolean, ctx?: ThemeServiceContext) => {
    const response = await api.patch(`${themesRoot(ctx)}/${themeId}/mobile-upload-pro`, { enabled });
    return response.data;
  },

  setMobileUploadLiteEnabled: async (themeId: string, enabled: boolean, ctx?: ThemeServiceContext) => {
    const response = await api.patch(`${themesRoot(ctx)}/${themeId}/mobile-upload-lite`, { enabled });
    return response.data;
  },

  setThemeAvailability: async (
    themeId: string,
    data: {
      scheduleEnabled: boolean;
      availableFrom?: string | null;
      availableUntil?: string | null;
      machineFilterEnabled: boolean;
      allowedMachineCodes: string[];
    },
    ctx?: ThemeServiceContext,
  ) => {
    const response = await api.patch(`${themesRoot(ctx)}/${themeId}/availability`, {
      ...data,
      source: 'web',
    });
    return response.data;
  },

  setProShowAllCustomFrames: async (proShowAllCustomFrames: boolean, ctx?: ThemeServiceContext) => {
    const response = await api.patch(`${themesRoot(ctx)}/pro-show-all-custom-frames`, {
      proShowAllCustomFrames,
      source: 'web',
    });
    return response.data;
  },

  updateThemeWithImage: async (themeId: string, data: UpdateThemeData, ctx?: ThemeServiceContext) => {
    const formData = new FormData();
    if (data.name) formData.append('name', data.name);
    if (data.description !== undefined) formData.append('description', data.description);
    if (data.tags) formData.append('tags', data.tags.join(','));
    if (data.isActive !== undefined) formData.append('isActive', data.isActive.toString());
    if (data.themeImage) formData.append('themeImage', data.themeImage);

    const response = await api.put(`${themesRoot(ctx)}/${themeId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteTheme: async (themeId: string, ctx?: ThemeServiceContext) => {
    const response = await api.delete(`${themesRoot(ctx)}/${themeId}`);
    return response.data;
  },

  addUserFrames: async (data: AddUserFramesData, ctx?: ThemeServiceContext) => {
    const response = await api.post(`${themesRoot(ctx)}/add-user-frames`, data);
    return response.data;
  },

  removeUserFrames: async (data: RemoveUserFramesData, ctx?: ThemeServiceContext) => {
    const response = await api.delete(`${themesRoot(ctx)}/user-frames`, { data });
    return response.data;
  },

  getThemeCustomFrameFrames: async (
    themeId: string,
    customFrameId: string,
    ctx?: ThemeServiceContext,
  ) => {
    const response = await api.get(
      `${themesRoot(ctx)}/${themeId}/custom-frames/${customFrameId}`,
    );
    return response.data;
  },

  addCustomFrameToTheme: async (
    themeId: string,
    customFrameId: string,
    ctx?: ThemeServiceContext,
  ) => {
    const response = await api.post(`${themesRoot(ctx)}/${themeId}/custom-frames`, {
      customFrameId,
    });
    return response.data;
  },

  removeCustomFrameFromTheme: async (
    themeId: string,
    customFrameId: string,
    ctx?: ThemeServiceContext,
  ) => {
    const response = await api.delete(
      `${themesRoot(ctx)}/${themeId}/custom-frames/${customFrameId}`,
    );
    return response.data;
  },

  reloadTheme: async (themeId: string, ctx?: ThemeServiceContext) => {
    const response = await api.post(`${themesRoot(ctx)}/${themeId}/reload`);
    return response.data;
  },
};
